import { parseWorkspaceEvent, workspaceEventsUrl } from "@/shared/api";
import { queryKeys } from "@/shared/query";
import type { QueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

export function useWorkspaceEvents(slug: string, queryClient: QueryClient) {
  const [state, setState] = useState<"connecting" | "live" | "offline">("connecting");

  useEffect(() => {
    const events = new EventSource(workspaceEventsUrl(slug), { withCredentials: true });
    events.onopen = () => setState("live");
    events.onerror = () => setState("offline");
    const receive = (message: MessageEvent) => {
      const event = parseWorkspaceEvent(message.data);
      if (!event) return;
      if (
        event.type === "lead.changed" ||
        event.type === "message.changed" ||
        event.type === "costs.changed" ||
        event.type === "api_costs.changed" ||
        event.type === "statistics.changed" ||
        event.type === "connection.changed" ||
        event.type === "workspace.changed"
      ) {
        void queryClient.invalidateQueries({ queryKey: ["polaris", slug, "statistics"] });
        if (
          event.type === "statistics.changed" ||
          event.type === "connection.changed" ||
          event.type === "workspace.changed"
        )
          void queryClient.invalidateQueries({ queryKey: queryKeys.statisticsSources(slug) });
      }
      if (
        event.type === "lead.changed" ||
        event.type === "message.changed" ||
        event.type === "proposal.changed" ||
        event.type === "action.changed" ||
        event.type === "followup.changed" ||
        event.type === "call.changed"
      ) {
        void queryClient.invalidateQueries({ queryKey: queryKeys.lead(slug, event.leadId) });
        void queryClient.invalidateQueries({ queryKey: ["polaris", slug, "leads"] });
      }
      // New messages, AI replies, programmed replies and the tick change "Da
      // rispondere" (ADR 0083); lead edits can close follow-ups; the AI's
      // preparations travel with their follow-up (ADR 0055).
      if (
        event.type === "followup.changed" ||
        event.type === "message.changed" ||
        event.type === "lead.changed" ||
        event.type === "proposal.changed" ||
        event.type === "action.changed" ||
        event.type === "workspace.changed"
      )
        void queryClient.invalidateQueries({ queryKey: queryKeys.today(slug) });
      // The approval cards show the lead and its unread messages too.
      if (
        event.type === "proposal.changed" ||
        event.type === "message.changed" ||
        event.type === "lead.changed"
      )
        void queryClient.invalidateQueries({ queryKey: queryKeys.proposals(slug) });
      if (
        event.type === "lead.changed" ||
        event.type === "message.changed" ||
        event.type === "action.changed"
      )
        void queryClient.invalidateQueries({
          queryKey: queryKeys.scheduledActions(slug, event.leadId),
        });
      // "Programmati" in the calendar: a send left, moved or was cancelled, also by
      // the lead writing (ADR 0069), or a lead was renamed.
      if (
        event.type === "lead.changed" ||
        event.type === "message.changed" ||
        event.type === "action.changed" ||
        event.type === "proposal.changed"
      )
        void queryClient.invalidateQueries({ queryKey: queryKeys.scheduledMessages(slug) });
      // A calendar changed: weeks, reminders, Oggi and the chat of the lead (ADR 0059).
      if (event.type === "appointment.changed") {
        void queryClient.invalidateQueries({ queryKey: queryKeys.appointments(slug) });
        void queryClient.invalidateQueries({ queryKey: queryKeys.today(slug) });
        // The overlaps shown on the appointment proposals in Approvazioni.
        void queryClient.invalidateQueries({ queryKey: queryKeys.proposals(slug) });
        if (event.leadId) {
          void queryClient.invalidateQueries({ queryKey: queryKeys.lead(slug, event.leadId) });
        }
      }
      if (event.type === "connection.changed" || event.type === "workspace.changed") {
        void queryClient.invalidateQueries({ queryKey: queryKeys.workspace(slug) });
      }
      // The templates' workers end with this event: sent, edited, deleted, synchronized.
      if (event.type === "workspace.changed") {
        void queryClient.invalidateQueries({ queryKey: queryKeys.templates(slug) });
        void queryClient.invalidateQueries({ queryKey: ["polaris", slug, "meta-conversions"] });
        void queryClient.invalidateQueries({ queryKey: ["polaris", slug, "lead"] });
      }
      // New estimates change the costs page and the prices in the open chats.
      if (event.type === "costs.changed") {
        void queryClient.invalidateQueries({ queryKey: ["polaris", slug, "whatsapp-costs"] });
        void queryClient.invalidateQueries({ queryKey: ["polaris", slug, "lead"] });
      }
      // What the AI is doing for a lead, and whether it works for everyone (ADR 0072).
      if (event.type === "ai.changed") {
        void queryClient.invalidateQueries({ queryKey: queryKeys.lead(slug, event.leadId) });
        void queryClient.invalidateQueries({ queryKey: queryKeys.aiHealth(slug) });
      }
      // A memo of the calendar was added, changed, done or deleted (ADR 0076).
      if (event.type === "memo.changed")
        void queryClient.invalidateQueries({ queryKey: queryKeys.memos(slug) });
      // Someone saved, moved or deleted a page of the wiki (ADR 0075).
      if (event.type === "wiki.changed")
        void queryClient.invalidateQueries({ queryKey: queryKeys.wiki(slug) });
      if (event.type === "ai_health.changed")
        void queryClient.invalidateQueries({ queryKey: queryKeys.aiHealth(slug) });
      // Apify runs settled or OpenAI's control read (ADR 0062).
      if (event.type === "api_costs.changed") {
        void queryClient.invalidateQueries({ queryKey: ["polaris", slug, "api-costs"] });
        void queryClient.invalidateQueries({ queryKey: ["polaris", slug, "lead"] });
      }
    };
    const eventNames = [
      "lead.changed",
      "message.changed",
      "proposal.changed",
      "action.changed",
      "followup.changed",
      "call.changed",
      "appointment.changed",
      "connection.changed",
      "costs.changed",
      "api_costs.changed",
      "statistics.changed",
      "workspace.changed",
      "ai.changed",
      "ai_health.changed",
      "wiki.changed",
      "memo.changed",
      "heartbeat",
    ];
    for (const name of eventNames) events.addEventListener(name, receive);
    return () => {
      for (const name of eventNames) events.removeEventListener(name, receive);
      events.close();
    };
  }, [queryClient, slug]);

  return state;
}
